import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ExternalLink, Check, X } from "lucide-react";

const TABS = [
  { key: "NEW", label: "New" },
  { key: "ACCEPTED", label: "Accepted" },
  { key: "DISMISSED", label: "Dismissed" },
];

export default function AdminInbox() {
  const [status, setStatus] = useState("NEW");
  const [items, setItems] = useState([]);
  const [dismissing, setDismissing] = useState(null);
  const [reason, setReason] = useState("");

  const load = () =>
    http
      .get("/admin/suggestions", { params: { status } })
      .then((r) => setItems(r.data.suggestions || []))
      .catch(() => setItems([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const accept = async (id) => {
    try {
      const r = await http.post(`/admin/suggestions/${id}/accept`);
      toast.success(`Accepted. Added to brand's extra_seed_urls: ${r.data.url}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    }
  };

  const dismiss = async () => {
    try {
      await http.post(`/admin/suggestions/${dismissing}/dismiss`, { reason });
      toast.success("Dismissed");
      setDismissing(null);
      setReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to dismiss");
    }
  };

  return (
    <AdminLayout>
      <div className="bp-container py-8" data-testid="admin-inbox-page">
        <h1 className="font-serif text-4xl mb-2">Inbox</h1>
        <p className="text-sm mb-6" style={{ color: "var(--bp-text-muted)" }}>
          Link submissions from brand representatives and the public. Accepting a suggestion
          queues its URL as a forced keyword-lane candidate on the brand's next pipeline run.
        </p>

        <div className="flex gap-2 mb-6" data-testid="inbox-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              className={`px-3 py-1 text-sm border ${status === t.key ? "bg-black text-white" : "bg-white"}`}
              style={{ borderColor: "var(--bp-border)" }}
              data-testid={`inbox-tab-${t.key.toLowerCase()}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Brand</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Category hint</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>URL</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Source</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Submitted</th>
                <th className="px-4 py-3 text-right uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: "var(--bp-text-muted)" }} data-testid="inbox-empty">
                    No suggestions in {status.toLowerCase()}.
                  </td>
                </tr>
              )}
              {items.map((s) => (
                <tr key={s.id} className="border-b align-top" style={{ borderColor: "var(--bp-border)" }} data-testid={`inbox-row-${s.id}`}>
                  <td className="px-4 py-3">
                    {s.brand ? (
                      <Link to={`/admin/brands/${s.brand.slug}`} className="bp-link font-serif">
                        {s.brand.display_name}
                      </Link>
                    ) : (
                      <span style={{ color: "var(--bp-text-muted)" }}>—</span>
                    )}
                    <div className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>
                      {s.brand?.sector_code || ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{s.category_hint || "—"}</td>
                  <td className="px-4 py-3 max-w-xs">
                    <a
                      href={s.suggested_url || s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="bp-link inline-flex items-center gap-1 break-all text-xs"
                    >
                      {s.suggested_url || s.url}
                      <ExternalLink size={12} />
                    </a>
                    {s.note && (
                      <div className="text-xs mt-1 italic" style={{ color: "var(--bp-text-muted)" }}>
                        "{s.note}"
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">
                    {s.source || (s.submitted_by_email ? "BRAND_REP" : "PUBLIC")}
                    {s.submitted_by_email && (
                      <div style={{ color: "var(--bp-text-muted)" }}>{s.submitted_by_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>
                    {(s.submitted_at || "").slice(0, 10)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {status === "NEW" ? (
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          onClick={() => accept(s.id)}
                          data-testid={`inbox-accept-${s.id}`}
                          disabled={!s.brand}
                        >
                          <Check size={14} className="mr-1" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDismissing(s.id)}
                          data-testid={`inbox-dismiss-${s.id}`}
                        >
                          <X size={14} className="mr-1" /> Dismiss
                        </Button>
                      </div>
                    ) : (
                      <div className="text-xs" style={{ color: "var(--bp-text-muted)" }}>
                        {status === "ACCEPTED" ? (
                          <span data-testid={`inbox-accepted-${s.id}`}>Accepted {s.accepted_at?.slice(0, 10)}</span>
                        ) : (
                          <span data-testid={`inbox-dismissed-${s.id}`}>
                            Dismissed {s.dismissed_at?.slice(0, 10)}
                            {s.dismiss_reason && <> — "{s.dismiss_reason}"</>}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {dismissing && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="dismiss-modal">
            <div className="bg-white border max-w-md w-full p-6" style={{ borderColor: "var(--bp-border)" }}>
              <h2 className="font-serif text-2xl mb-4">Dismiss suggestion</h2>
              <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>Reason (optional)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. not a public source, duplicates existing evidence, off-topic"
                data-testid="dismiss-reason-input"
              />
              <div className="flex gap-2 justify-end mt-4">
                <Button variant="outline" onClick={() => { setDismissing(null); setReason(""); }} data-testid="dismiss-cancel">Cancel</Button>
                <Button onClick={dismiss} data-testid="dismiss-confirm">Confirm dismiss</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
